import React, { useMemo, useState, useCallback, useEffect, lazy, Suspense, useRef } from 'react';
import styles from './GradeItem.module.scss';

import IconArrowUp from 'src/assets/icons/dropdown-white.svg?react';
import PDFIcon from "@assets/icons/wired-outline-pdf.svg?react";
import PlusIcon from "@assets/icons/wired-outline-plus.svg?react";
import { darkColors, IconStandalone, transitionsScopeSequences } from '@src/constants';
import LessonProgressModal from '@src/components/lesson-progress-modal/LessonProgressModal';
import Button from '@src/components/button/Button';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import { getIconByTitle, getRandColor } from '@src/utils/helpers';
import { CollectionLessonFragment, NormalMultiCollectionFragment, useGetMultiCollectionLazyQuery } from '@src/api/graphql-codegen';
import Spinner from '@src/components/spinner/Spinner';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const LazyCollection = lazy(() => import('./LazyCollection/LazyCollection'));

type Props = {
  gradeId: string;
  gradeName: string;
  selectedId?: string | null;
  selected?: boolean;
  handleSelected?: (program: any) => void;
  handleNoneSelect?: () => void;
  refresh?: () => void;
  multiCollectionData?: NormalMultiCollectionFragment;
};

function GradeItem(props: Props) {
  const {
    gradeId,
    gradeName,
    selectedId,
    selected,
    handleSelected,
    handleNoneSelect,
    refresh,
    multiCollectionData,
  } = props;

  const [showLesson, setShowLesson] = useState(false);
  const [showContentId, setShowContentId] = useState<string | undefined>(undefined);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const [isVisible, setIsVisible] = useState(false);

  const [getMultiCollection, {data, loading}] = useGetMultiCollectionLazyQuery({fetchPolicy: 'network-only'});

  const isSelected = useMemo(() => {
    return (selected && selectedId === gradeId) ? true : false;
  }, [selected, selectedId, gradeId]);

  useEffect(() => {
    if (!multiCollectionData && isVisible) {
      getMultiCollection({variables: {input: {id: gradeId}},});
    }
  }, [multiCollectionData, gradeId, isVisible, getMultiCollection])

  // Merge: prefer batch data, fall back to individual query result
  const mcData = multiCollectionData ?? data?.getMultiCollection;

  useEffect(() => {
    const node = wrapperRef.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 } // Adjust the threshold as needed
    );
    if (node) {
      observer.observe(node);
    }
    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
}, []);


  const handleOpenContent = useCallback((content: CollectionLessonFragment) => {
    setShowLesson(true);
    setShowContentId(content.id);
  }, [])

  const hoverTitleViewStyle = useMemo(() => {
    const classes = [styles.hoverTitleView];
    
    return classes.join(' ');
  }, [])


  const handleCloseModal = useCallback(async() => {
    setShowLesson(false)
    setShowContentId(undefined)
    const unitPage = document.getElementById("unitsPage")
    if (unitPage) {
      unitPage.style.paddingBottom = '0';
    }
    if (refresh) {
      refresh()
    }
  }, [refresh]);

  const handleSelectGrade = useCallback(() => {
    handleSelected?.(gradeId)
  }, [handleSelected, gradeId])

  const Icon = getIconByTitle(gradeName.replace('Transitions (Rename)', '').trim());

  const collectionList = useMemo(() => {
    if (!mcData?.content) return []
    return (mcData?.content ?? []).map(collection => {
      const color = getRandColor(mcData?.content ?? [], darkColors)
      return {
        ...collection,
        color: color,
      }
    })
  }, [mcData?.content])

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <div className={styles.backdrop} />
      <div className={styles.card}>
        <div className={styles.gradeTitleView}>
          <div className={styles.gradeGrayView}>
            <div className={styles.titleSection}>
              <Icon />
              <h1>{gradeName}</h1>
            </div>
            <a
              href={transitionsScopeSequences[gradeName]}
              target={"_blank"}
              className={styles.scopePdfView}
              rel="noreferrer"
            >
              <PDFIcon />
              <span>Lesson Overviews</span>
              <PlusIcon />
            </a>
          </div>
        </div>
        <div className={styles.topUnitView} style={{maxHeight: isSelected ? 'min-content' : '586px'}}>
          {loading ? <div className={styles.loadingWrapper}><Spinner /></div> :
            <DndProvider backend={HTML5Backend}>
              <Suspense fallback={<Spinner />}>
                {collectionList.map((theme, index) => {
                  return (
                    <LazyCollection key={index} collection={theme} handleOpenContent={handleOpenContent} />
                  )
                })
                }
              </Suspense>
            </DndProvider>
        }
        </div>
        <div className={!isSelected ? styles.gradientBackground : styles.whiteBackground}></div>
        {/* <div style={{height: '50px'}}></div> */}
        {<div>
          <div className={hoverTitleViewStyle}>
            <div className={styles.hoverTitleSubView}>
              <Button size='small' className={styles.extendViewButton} label={isSelected ? "View Less" : "View Lessons"}
                leftIcon={<LordIcon size={30} src={IconStandalone} isHover={true} colors={{primary: '#FFFFFF', secondary: '#FFFFFF'}} target='.itemWrapper__bMNcg' />}
                rightIcon={<IconArrowUp className={[styles.arrowIcon, isSelected ? styles.materialToggleClose : styles.materialToggleOpen].join(' ')} width={13} />} onClick={isSelected ? handleNoneSelect : handleSelectGrade}/>
            </div>
          </div>
        </div>}
      </div>
      {showContentId && showLesson ?
        <LessonProgressModal
          open={showLesson} onClose={handleCloseModal}
          lessonId={showContentId} gradeName={gradeName}
          mcId={mcData?.id ?? ""}
          mcName={mcData?.name ?? ""}
          programName='Transitions'
        /> : null}
    </div>
  );
}

export default GradeItem;
